import { useEffect, useRef } from 'react';

import { useActiveElement } from '../../hooks/useActiveElement.js';
import { useFocusContext } from '../../hooks/useFocusContext.js';
import { useModality } from '../../hooks/useModality.js';
import { type DataTestId } from '../../types/data-props.js';
import type { WithChildren } from '../../types/with-children.js';
import { focusFirstDescendant } from '../../utils/focus-management/focus-first-descendant.js';

// #region Types

/**
 * Prop definition of the `FocusScope` component.
 * @public
 */
export interface FocusScopeProps extends WithChildren, DataTestId {
  /**
   * If set to true, the focus scope automatically focuses the first focusable element inside.
   * @defaultValue false
   */
  autoFocus?: boolean;
  /**
   * If set to true, the focus scope traps the focus inside.
   * @defaultValue false
   */
  contain?: boolean;
}
// #endregion Types

/**
 * The `FocusScope` helps you manage the focus when opening any overlay. If you
 * want to create a custom overlay, you can wrap the content of your overlay in the
 * `FocusScope` to help you make the overlay accessible and handle focus
 * appropriately.
 * @public
 */
export const FocusScope = (props: FocusScopeProps) => {
  const { autoFocus, contain, children, 'data-testid': dataTestId } = props;
  const focusContext = useFocusContext();
  const modality = useModality();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusedElement = useActiveElement();
  const triggerElement = useRef(focusedElement);

  let focusOptions: { focusVisible: boolean } | undefined;

  // If the modality is keyboard, we want to focus the first focusable element and show the focus ring.
  if (modality === 'keyboard') {
    focusOptions = { focusVisible: true };
  } else if (
    // If the modality is pointer or touch, we want to focus the first focusable element but not show the focus ring.
    modality === 'pointer' ||
    modality === 'touch'
  ) {
    focusOptions = { focusVisible: false };
  }

  useEffect(() => {
    if (contain || autoFocus) {
      focusFirstDescendant(wrapperRef.current, focusOptions);
    }

    if (contain) {
      focusContext.setBackgroundInertness(true);
      const triggerElementCopy = triggerElement.current;

      return () => {
        focusContext.setBackgroundInertness(false);

        focusContext.focusTriggerElement(triggerElementCopy);
      };
    }

    if (!autoFocus && !contain) {
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') {
          return;
        }

        if (e.target === triggerElement.current) {
          e.preventDefault();
          focusFirstDescendant(wrapperRef.current, focusOptions);
        }
      });
    }

    const triggerElementCopy = triggerElement.current;

    return () => {
      // restore focus on trigger when unmounting the focusscope
      triggerElementCopy.focus();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contain, autoFocus]);

  return (
    <div
      style={{ display: 'contents' }}
      ref={wrapperRef}
      aria-labelledby={triggerElement.current.id}
      data-testid={dataTestId}
      // Prevents that focus is lost when clicking on non-focusable items inside (atm used in the internal Drawer).
      // Probably worth considering moving that to the focusScope when redoing the overlays.
      // tabIndex={-1}
    >
      {children}
    </div>
  );
};

FocusScope.displayName = 'FocusScope';
